import { msg } from "@lingui/core/macro";
import { type Component, For, Show } from "solid-js";
import {
  BarChart3,
  CheckCircle2,
  ListTodo,
  type LucideProps,
  ShieldOff,
  TrendingUp,
  UserCheck,
  Users,
  Wifi,
} from "@fleet/icons";
import { Card, CardContent, Skeleton } from "@fleet/ui";
import { ServerDataGate } from "@/components/ui/server-data-gate";
import { api, createApiQuery } from "@/lib/api";
import { adminQueryKeys, type components } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { CrmFunnelSection } from "@/features/crm/funnel-section";
import { useActiveOrg } from "@/features/academy/hooks/use-active-org";
import { AcademyMetricsSections } from "@/features/academy/metrics/dashboard";
import { ClientTelemetryPanel } from "@/features/admin/components/client-telemetry-panel";

type ErrorModel = components["schemas"]["ErrorModel"];
type AdminMetrics = components["schemas"]["AdminMetrics"];

interface Stat {
  label: string;
  value: number;
  icon: Component<LucideProps>;
}

export function MetricsDashboard() {
  const { t } = useI18n();
  const { activeOrgId, activeOrg } = useActiveOrg();

  const query = createApiQuery<AdminMetrics, ErrorModel>(
    () => adminQueryKeys.metrics(),
    async () => api.GET("/api/admin/metrics", {}),
    () => ({ staleTime: 30_000 }),
  );

  const stats = (): Stat[] => {
    const m = query.data;
    if (!m) {
      return [];
    }
    return [
      { label: t(msg`Total users`), value: m.totalUsers, icon: Users },
      { label: t(msg`Verified users`), value: m.verifiedUsers, icon: UserCheck },
      { label: t(msg`Banned users`), value: m.bannedUsers, icon: ShieldOff },
      { label: t(msg`Active sessions`), value: m.activeSessions, icon: Wifi },
      { label: t(msg`New signups (7d)`), value: m.newSignups7d, icon: TrendingUp },
      { label: t(msg`New signups (30d)`), value: m.newSignups30d, icon: TrendingUp },
      { label: t(msg`Total courses`), value: m.totalCourses, icon: ListTodo },
      { label: t(msg`Published courses`), value: m.publishedCourses, icon: CheckCircle2 },
    ];
  };

  return (
    <div class="space-y-6">
      <div class="flex items-center gap-3">
        <BarChart3 class="h-8 w-8 text-primary" />
        <div>
          <h2 class="text-3xl font-bold tracking-tight">{t(msg`Metrics`)}</h2>
          <p class="text-sm text-muted-foreground">{t(msg`At-a-glance system health.`)}</p>
        </div>
      </div>

      {/* LF-B: server-only metrics — offline shows an honest state, not
          eternal skeletons (query pending+paused with no network). */}
      <ServerDataGate
        pending={query.isLoading}
        error={query.isError}
        refetch={() => void query.refetch()}
        skeleton={
          <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <For each={Array.from({ length: 8 }, () => 0)}>
              {() => <Skeleton class="h-28 w-full rounded-xl" />}
            </For>
          </div>
        }
      >
        <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <For each={stats()}>
            {(stat) => (
              <Card>
                <CardContent class="p-5 space-y-2">
                  <div class="flex items-center justify-between">
                    <span class="text-sm text-muted-foreground">{stat.label}</span>
                    <stat.icon class="h-4 w-4 text-primary" />
                  </div>
                  <div class="text-3xl font-bold tracking-tight">{stat.value.toLocaleString()}</div>
                </CardContent>
              </Card>
            )}
          </For>
        </div>
      </ServerDataGate>

      {/* Client version census + operational beacons (docs/CLIENT_TELEMETRY.md). */}
      <Show when={query.data?.clientTelemetry}>
        {(telemetry) => <ClientTelemetryPanel telemetry={telemetry()} />}
      </Show>

      {/* F3-I (CRM): lead-capture funnel + sources for the active academy. */}
      <CrmFunnelSection />

      {/* F4-C: analytics rollup for the active ORGANIZATION (enrollment, revenue,
          debt, attendance, academic). The back-office picks the academy with the
          switcher; the org-scoped endpoint answers via its platform ViewMetrics. */}
      <Show when={activeOrgId()}>
        <div class="space-y-4 border-t pt-6">
          <div class="flex items-center gap-3">
            <BarChart3 class="h-6 w-6 text-primary" />
            <div>
              <h3 class="text-xl font-bold tracking-tight">{t(msg`Academy rollup`)}</h3>
              <p class="text-sm text-muted-foreground">
                {activeOrg()?.name ?? t(msg`Active organization`)}
              </p>
            </div>
          </div>
          <AcademyMetricsSections orgId={activeOrgId} />
        </div>
      </Show>
    </div>
  );
}
